import React, { useState, useEffect, useRef } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import './TimesheetPage.css';
import { toast } from '../components/Toast';

const EMPTY_FORM = { workDate: '', projectId: '', startTime: '', endTime: '', hoursWorked: '', description: '' };

function TimesheetPage() {
  const { user, hasMenuAccess } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectPage, setProjectPage] = useState(0);
  const [projectTotalPages, setProjectTotalPages] = useState(1);
  const [projectTotalElements, setProjectTotalElements] = useState(0);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const projectPickerRef = useRef(null);

  const [myEmployeeId, setMyEmployeeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // timesheetId being edited
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const isManager = ['COMPANY_ADMIN', 'BRANCH_MANAGER', 'SUPERVISOR'].includes(user?.role) || hasMenuAccess('timesheets');

  useEffect(() => {
    fetchTimesheets();
    fetchProjects(0, '');
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (projectPickerRef.current && !projectPickerRef.current.contains(e.target)) {
        setProjectDropdownOpen(false);
      }
    };
    if (projectDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [projectDropdownOpen]);

  const fetchMyEmployeeId = async () => {
    if (myEmployeeId) return myEmployeeId;
    const res = await axios.get('/employees/my-profile');
    const emp = res.data?.data || res.data;
    setMyEmployeeId(emp?.id);
    return emp?.id;
  };

  const fetchTimesheets = async () => {
    setLoading(true);
    try {
      let data = [];
      if (isManager) {
        const res = await axios.get('/employees/timesheets');
        data = res.data?.timesheets || res.data?.data || [];
      } else {
        const profileRes = await axios.get('/employees/my-profile');
        const emp = profileRes.data?.data || profileRes.data;
        if (emp?.id) {
          setMyEmployeeId(emp.id);
          const res = await axios.get(`/employees/${emp.id}/timesheets`);
          data = res.data?.timesheets || res.data?.data?.timesheets || [];
        }
      }
      setTimesheets(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async (page = 0, search = '') => {
    setProjectLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('size', '20');
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      const res = await axios.get(`/projects?${params.toString()}`);
      const content = res.data?.data?.content || res.data?.content || [];
      const totalPages = res.data?.data?.totalPages || res.data?.totalPages || 1;
      const totalElements = res.data?.data?.totalElements || res.data?.totalElements || 0;
      setProjects(Array.isArray(content) ? content : []);
      setProjectPage(page);
      setProjectTotalPages(totalPages);
      setProjectTotalElements(totalElements);
    } catch (e) {
      console.error('Failed to fetch projects:', e);
    } finally {
      setProjectLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setSelectedProject(null);
    setProjectSearch('');
    fetchProjects(0, '');
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      workDate: t.workDate || '',
      projectId: t.projectId || '',
      startTime: t.startTime ? t.startTime.slice(0, 5) : '',
      endTime: t.endTime ? t.endTime.slice(0, 5) : '',
      hoursWorked: t.hoursWorked != null ? String(t.hoursWorked) : '',
      description: t.description || '',
    });
    setFormErrors({});
    if (t.projectId) {
      const existing = projects.find(p => String(p.id) === String(t.projectId));
      setSelectedProject(existing || { id: t.projectId, name: t.projectName || `Project #${t.projectId}` });
    } else {
      setSelectedProject(null);
    }
    setProjectSearch('');
    fetchProjects(0, '');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setSelectedProject(null);
    setProjectDropdownOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'startTime' || name === 'endTime') {
        const start = name === 'startTime' ? value : prev.startTime;
        const end   = name === 'endTime'   ? value : prev.endTime;
        if (start && end) {
          const [sh, sm] = start.split(':').map(Number);
          const [eh, em] = end.split(':').map(Number);
          let mins = (eh * 60 + em) - (sh * 60 + sm);
          if (mins < 0) mins += 24 * 60;
          updated.hoursWorked = (mins / 60).toFixed(2);
        }
      }
      return updated;
    });
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.workDate) errs.workDate = 'Work date is required';
    const hours = parseFloat(form.hoursWorked);
    if (!form.hoursWorked || isNaN(hours) || hours <= 0) errs.hoursWorked = 'Enter valid hours';
    else if (hours > 24) errs.hoursWorked = 'Cannot exceed 24 hours';
    if (form.startTime && form.endTime && form.startTime === form.endTime)
      errs.endTime = 'End time must differ from start time';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      let empId = myEmployeeId;
      if (!empId) {
        empId = await fetchMyEmployeeId();
      }
      if (!empId) { toast.error('Employee profile not found'); setSubmitting(false); return; }

      const payload = {
        workDate: form.workDate,
        hoursWorked: parseFloat(form.hoursWorked),
        startTime: form.startTime || null,
        endTime: form.endTime || null,
        description: form.description,
        projectId: form.projectId || null,
      };

      if (editingId) {
        await axios.put(`/employees/${empId}/timesheets/${editingId}`, payload);
        flash('Timesheet updated');
      } else {
        await axios.post(`/employees/${empId}/timesheets`, payload);
        flash('Timesheet submitted');
      }
      closeForm();
      fetchTimesheets();
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || `Failed to ${editingId ? 'update' : 'submit'} timesheet`);
    } finally {
      setSubmitting(false);
    }
  };

  const flash = (msg) => {
    toast.success(msg);
  };

  const totalHours = timesheets.reduce((sum, t) => sum + parseFloat(t.hoursWorked || 0), 0);

  return (
    <div className="ts-container">
      <div className="ts-header">
        <div>
          <h1 className="ts-title">Timesheets</h1>
          <p className="ts-subtitle">
            {isManager ? 'All timesheets in your branch' : 'Your submitted timesheets'}
          </p>
        </div>
        {!isManager && (
          <button className="ts-btn-primary" onClick={openCreate}>+ Submit Timesheet</button>
        )}
      </div>

      {/* Summary */}
      <div className="ts-summary">
        <div className="ts-stat">
          <span className="ts-stat-value">{timesheets.length}</span>
          <span className="ts-stat-label">Total Entries</span>
        </div>
        <div className="ts-stat">
          <span className="ts-stat-value">{totalHours.toFixed(1)}</span>
          <span className="ts-stat-label">Total Hours</span>
        </div>
        <div className="ts-stat">
          <span className="ts-stat-value">{timesheets.filter(t => t.approvedByName).length}</span>
          <span className="ts-stat-label">Approved</span>
        </div>
        <div className="ts-stat">
          <span className="ts-stat-value">{timesheets.filter(t => !t.approvedByName).length}</span>
          <span className="ts-stat-label">Pending</span>
        </div>
      </div>

      {/* Form (create or edit) */}
      {showForm && (
        <div className="ts-form-card">
          <div className="ts-form-header">
            <h3>{editingId ? 'Edit Timesheet' : 'New Timesheet Entry'}</h3>
            <button className="ts-close" onClick={closeForm}>&times;</button>
          </div>
          <form onSubmit={handleSubmit} className="ts-form">
            <div className="ts-form-row">
              <div className="ts-form-group">
                <label>Work Date *</label>
                <input type="date" name="workDate" value={form.workDate} onChange={handleChange}
                  className={formErrors.workDate ? 'error' : ''} max={new Date().toISOString().split('T')[0]} />
                {formErrors.workDate && <span className="ts-error">{formErrors.workDate}</span>}
              </div>
              <div className="ts-form-group ts-project-picker-wrap" ref={projectPickerRef}>
                <label>Project</label>
                <div
                  className="ts-project-picker-trigger"
                  onClick={() => setProjectDropdownOpen(prev => !prev)}
                  tabIndex={0}
                >
                  <span className={selectedProject ? 'ts-project-chosen' : 'ts-project-placeholder'}>
                    {selectedProject ? selectedProject.name : 'Select a Project (or No Project)'}
                  </span>
                  <span className="ts-project-arrow">{projectDropdownOpen ? '▲' : '▼'}</span>
                </div>

                {projectDropdownOpen && (
                  <div className="ts-project-dropdown">
                    <div className="ts-project-search-box">
                      <input
                        type="text"
                        placeholder="Search projects..."
                        value={projectSearch}
                        onChange={(e) => {
                          const val = e.target.value;
                          setProjectSearch(val);
                          fetchProjects(0, val);
                        }}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                      {projectSearch && (
                        <button
                          type="button"
                          className="ts-project-clear-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setProjectSearch('');
                            fetchProjects(0, '');
                          }}
                        >
                          &times;
                        </button>
                      )}
                    </div>

                    <div className="ts-project-list">
                      <div
                        className={`ts-project-option ${!form.projectId ? 'is-selected' : ''}`}
                        onClick={() => {
                          setForm(prev => ({ ...prev, projectId: '' }));
                          setSelectedProject(null);
                          setProjectDropdownOpen(false);
                        }}
                      >
                        <span className="ts-project-option-name"><em>No Project</em></span>
                        {!form.projectId && <span className="ts-project-check">✓</span>}
                      </div>

                      {projectLoading ? (
                        <div className="ts-project-loading">Loading projects...</div>
                      ) : projects.length === 0 ? (
                        <div className="ts-project-empty">No projects found</div>
                      ) : (
                        projects.map(p => {
                          const isSelected = String(form.projectId) === String(p.id);
                          return (
                            <div
                              key={p.id}
                              className={`ts-project-option ${isSelected ? 'is-selected' : ''}`}
                              onClick={() => {
                                setForm(prev => ({ ...prev, projectId: p.id }));
                                setSelectedProject(p);
                                setProjectDropdownOpen(false);
                              }}
                            >
                              <div className="ts-project-option-info">
                                <span className="ts-project-option-name">{p.name}</span>
                                {p.customerName && (
                                  <span className="ts-project-option-sub">{p.customerName}</span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {p.status && (
                                  <span className={`ts-project-status-tag status-${p.status.toLowerCase()}`}>
                                    {p.status}
                                  </span>
                                )}
                                {isSelected && <span className="ts-project-check">✓</span>}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className="ts-project-pagination" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="ts-page-btn"
                        disabled={projectPage <= 0 || projectLoading}
                        onClick={() => fetchProjects(projectPage - 1, projectSearch)}
                      >
                        ◀ Prev
                      </button>
                      <span className="ts-page-info">
                        Page {projectTotalPages > 0 ? projectPage + 1 : 0} of {projectTotalPages}
                        {projectTotalElements > 0 && ` (${projectTotalElements})`}
                      </span>
                      <button
                        type="button"
                        className="ts-page-btn"
                        disabled={projectPage >= projectTotalPages - 1 || projectLoading}
                        onClick={() => fetchProjects(projectPage + 1, projectSearch)}
                      >
                        Next ▶
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="ts-form-row">
              <div className="ts-form-group">
                <label>Start Time</label>
                <input type="time" name="startTime" value={form.startTime} onChange={handleChange} />
              </div>
              <div className="ts-form-group">
                <label>End Time</label>
                <input type="time" name="endTime" value={form.endTime} onChange={handleChange}
                  className={formErrors.endTime ? 'error' : ''} />
                {formErrors.endTime && <span className="ts-error">{formErrors.endTime}</span>}
              </div>
            </div>
            <div className="ts-form-group">
              <label>Hours Worked *</label>
              <div className="ts-hours-row">
                <input type="number" name="hoursWorked" value={form.hoursWorked} onChange={handleChange}
                  placeholder="e.g. 8" step="0.25" min="0.25" max="24"
                  className={formErrors.hoursWorked ? 'error' : ''} />
                {form.startTime && form.endTime && (
                  <span className="ts-auto-badge">auto-calculated</span>
                )}
              </div>
              {formErrors.hoursWorked && <span className="ts-error">{formErrors.hoursWorked}</span>}
            </div>
            <div className="ts-form-group">
              <label>Description</label>
              <textarea name="description" value={form.description} onChange={handleChange}
                rows={3} placeholder="What did you work on?" />
            </div>
            <div className="ts-form-actions">
              <button type="button" className="ts-btn-secondary" onClick={closeForm}>Cancel</button>
              <button type="submit" className="ts-btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : (editingId ? 'Update' : 'Submit')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="ts-loading">Loading timesheets...</div>
      ) : timesheets.length === 0 ? (
        <div className="ts-empty">
          <div className="ts-empty-icon">⏱</div>
          <p>No timesheets yet.{!isManager && ' Submit your first entry above.'}</p>
        </div>
      ) : (
        <div className="ts-table-wrap">
          <table className="ts-table">
            <thead>
              <tr>
                {isManager && <th>Employee</th>}
                <th>Date</th>
                <th>Start</th>
                <th>End</th>
                <th>Hours</th>
                <th>Project</th>
                <th>Description</th>
                <th>Status</th>
                {!isManager && <th></th>}
              </tr>
            </thead>
            <tbody>
              {timesheets.map(t => {
                const isPending = !t.approvedByName;
                return (
                  <tr key={t.id}>
                    {isManager && <td data-label="Employee"><strong>{t.employeeName || '—'}</strong></td>}
                    <td data-label="Date">{t.workDate ? new Date(t.workDate).toLocaleDateString() : '—'}</td>
                    <td data-label="Start">{t.startTime ? t.startTime.slice(0, 5) : <span className="ts-muted">—</span>}</td>
                    <td data-label="End">{t.endTime   ? t.endTime.slice(0, 5)   : <span className="ts-muted">—</span>}</td>
                    <td data-label="Hours"><span className="ts-hours">{t.hoursWorked}h</span></td>
                    <td data-label="Project">{t.projectName || <span className="ts-muted">—</span>}</td>
                    <td data-label="Description" className="ts-desc">{t.description || <span className="ts-muted">—</span>}</td>
                    <td data-label="Status">
                      <span className={`ts-badge ${isPending ? 'ts-badge-pending' : 'ts-badge-approved'}`}>
                        {isPending ? 'Pending' : 'Approved'}
                      </span>
                    </td>
                    {!isManager && (
                      <td data-label="Actions">
                        {isPending && (
                          <div className="action-buttons">
                            <button className="btn btn-sm btn-secondary" onClick={() => openEdit(t)} title="Edit">
                              ✏️ Edit
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TimesheetPage;
